export type AutoHideMode = "none" | "timeout" | "scroll";
export type AutoHideState =
  | "idle"
  | "armed"
  | "engaged"
  | "auto-hidden"
  | "consented"
  | "destroyed";

export type AutoHideOptions = {
  mode: AutoHideMode;
  delaySeconds: number;
  scrollPercent: number;
};

export type AutoHideCallbacks = {
  hide: () => void;
  reportAutoHide: (trigger: "timeout" | "scroll") => void;
};

/**
 * Owns the non-consent lifecycle of an auto-hidden banner. It deliberately
 * never receives a consent API, so hiding cannot be mistaken for a choice.
 */
export class AutoHideController {
  private state: AutoHideState = "idle";
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private timeoutStartedAt = 0;
  private remainingTimeoutMs = 0;
  private readonly onScroll = () => this.handleScroll();
  private readonly onVisibilityChange = () => this.handleVisibilityChange();

  constructor(
    private readonly options: AutoHideOptions,
    private readonly callbacks: AutoHideCallbacks,
  ) {}

  arm(): void {
    if (this.state !== "idle" || this.options.mode === "none") return;

    this.state = "armed";
    if (this.options.mode === "timeout") {
      this.remainingTimeoutMs = this.options.delaySeconds * 1_000;
      document.addEventListener("visibilitychange", this.onVisibilityChange);
      if (!document.hidden) this.scheduleTimeout();
      return;
    }

    window.addEventListener("scroll", this.onScroll, { passive: true });
  }

  engage(): void {
    if (this.state !== "armed") return;
    this.clearActiveTrigger();
    this.state = "engaged";
  }

  consented(): void {
    if (this.state === "destroyed" || this.state === "auto-hidden") return;
    this.clearActiveTrigger();
    this.state = "consented";
  }

  destroy(): void {
    if (this.state === "destroyed") return;
    this.clearActiveTrigger();
    this.state = "destroyed";
  }

  getState(): AutoHideState {
    return this.state;
  }

  private handleScroll(): void {
    if (this.state !== "armed" || document.hidden) return;

    const documentHeight = Math.max(
      document.documentElement.scrollHeight,
      document.body?.scrollHeight ?? 0,
    );
    const scrollableHeight = documentHeight - window.innerHeight;
    if (scrollableHeight <= 0) return;

    const progress = Math.max(0, Math.min(100, (window.scrollY / scrollableHeight) * 100));
    if (progress >= this.options.scrollPercent) {
      this.autoHide("scroll");
    }
  }

  private autoHide(trigger: "timeout" | "scroll"): void {
    if (this.state !== "armed") return;
    this.clearActiveTrigger();
    this.state = "auto-hidden";
    this.callbacks.hide();
    this.callbacks.reportAutoHide(trigger);
  }

  private clearActiveTrigger(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    window.removeEventListener("scroll", this.onScroll);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.timeoutStartedAt = 0;
    this.remainingTimeoutMs = 0;
  }

  private scheduleTimeout(): void {
    if (this.state !== "armed" || this.remainingTimeoutMs <= 0) return;
    this.timeoutStartedAt = Date.now();
    this.timeoutId = setTimeout(
      () => this.autoHide("timeout"),
      this.remainingTimeoutMs,
    );
  }

  private handleVisibilityChange(): void {
    if (this.state !== "armed" || this.options.mode !== "timeout") return;

    if (document.hidden) {
      if (this.timeoutId === null) return;
      const elapsed = Math.max(0, Date.now() - this.timeoutStartedAt);
      this.remainingTimeoutMs = Math.max(0, this.remainingTimeoutMs - elapsed);
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
      return;
    }

    this.scheduleTimeout();
  }
}
